import * as Yup from "yup";
import {
  buildPhoneNumberSchema,
  optionalPhoneNumberSchema,
} from "../pages/ThreePL/shared/phoneValidation";

const emptyDateToNull = (value, originalValue) =>
  originalValue === "" || originalValue === null || originalValue === undefined
    ? null
    : value;

const isDateOnOrAfter = (value, compareValue) => {
  if (!value || !compareValue) return true;

  const currentDate = new Date(value);
  const compareDate = new Date(compareValue);

  if (Number.isNaN(currentDate.getTime()) || Number.isNaN(compareDate.getTime())) {
    return true;
  }

  return currentDate.getTime() >= compareDate.getTime();
};

const completionDateAfterStartDateMessage =
  "Completion date & time cannot be earlier than start date";

const shouldRequireTripStopProductDetails = (stopType = "") => {
  const normalizedStopType = String(stopType || "").trim().toLowerCase();
  return normalizedStopType !== "empty" && normalizedStopType !== "return";
};

const tripStopProductSchema = Yup.string()
  .trim()
  .when("stopType", {
    is: shouldRequireTripStopProductDetails,
    then: (schema) => schema.required("Product is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  });

const tripStopLoadTypeSchema = Yup.string()
  .trim()
  .when("stopType", {
    is: shouldRequireTripStopProductDetails,
    then: (schema) => schema.required("Load Type is required"),
    otherwise: (schema) => schema.notRequired().nullable(),
  });

const buildTripStopQuantitySchema = (positiveMessage) =>
  Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return null;
      }

      return value;
    })
    .nullable()
    .when("stopType", {
      is: (stopType) => shouldRequireTripStopProductDetails(stopType),
      then: (schema) =>
        schema
          .typeError("Product Quantity must be a number")
          .positive(positiveMessage)
          .required("Product Quantity is required"),
      otherwise: (schema) =>
        schema
          .typeError("Product Quantity must be a number")
          .positive(positiveMessage)
          .notRequired()
          .nullable(),
    })
    .test(
      "unload-limit",
      "Unload quantity limit exceeded",
      function (value) {
        if (value === undefined || value === null || isNaN(value)) return true;

        const currentStop = this.parent;
        const stopType = String(currentStop?.stopType || "").toLowerCase();
        if (stopType !== "unload") return true;

        const fromList = this.options.from || [];
        const stopsObj = fromList.find((f) => Array.isArray(f?.value) || Array.isArray(f?.value?.stops));
        const stops = stopsObj ? (Array.isArray(stopsObj.value) ? stopsObj.value : stopsObj.value.stops) : [];

        if (!stops.length) return true;

        const path = this.path || "";
        const match = path.match(/stops\[(\d+)\]/);
        const currentIndex = match ? parseInt(match[1], 10) : -1;

        const totalLoad = stops
          .filter((stop) => String(stop?.stopType || "").toLowerCase() === "load")
          .reduce((sum, stop) => sum + (Number(stop?.quantity) || 0), 0);

        const otherUnloadSum = stops
          .filter((stop, index) => index !== currentIndex && String(stop?.stopType || "").toLowerCase() === "unload")
          .reduce((sum, stop) => sum + (Number(stop?.quantity) || 0), 0);

        const totalUnloadWithThis = otherUnloadSum + Number(value);

        if (totalUnloadWithThis > totalLoad) {
          const remaining = Math.max(0, totalLoad - otherUnloadSum);
          return this.createError({
            message: `Unload quantity exceeds remaining load limit of ${remaining}.`,
          });
        }

        return true;
      }
    );

const tripStopQuantitySchema = buildTripStopQuantitySchema(
  "Product Quantity must be positive"
);
const tripDetailStopQuantitySchema = buildTripStopQuantitySchema(
  "Product Quantity must be greater than 0"
);

export const validationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(30, "Name must not exceed 30 characters")
    .matches(
      /^[a-zA-Z\s-]+$/,
      "Name can only contain letters, spaces, and hyphens"
    )
    .required("Name is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .max(50, "Email must not exceed 50 characters")
    .required("Email is required"),
  role_id: Yup.mixed().required("Role is required"),
});

export const permissionValidationSchema = Yup.object().shape({
  name: Yup.string()
    .max(30, "Permission Name must not exceed 30 characters")
    .required("Permission Name is required"),
  description: Yup.string()
    .max(255, "Description must not exceed 255 characters")
    .required("description is required"),
});



export const editUserValidationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Name must be at least 2 characters")
    .max(30, "Name must not exceed 30 characters")
    .matches(
      /^[a-zA-Z\s-]+$/,
      "Name can only contain letters, spaces, and hyphens"
    )
    .required("Name is required"),
  role_id: Yup.mixed().required("Role is required"),
});

export const loginSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .max(50, "Email must not exceed 50 characters")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

export const forgotSchema = Yup.object().shape({
  email: Yup.string()
    .email("Please enter a valid email address")
    .max(50, "Email must not exceed 50 characters")
    .required("Email is required"),
});

export const resetSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Password must contain at least one uppercase letter, one lowercase letter, and one number"
    )
    .required("Password is required"),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

export const registerSchema = Yup.object().shape({
  username: Yup.string()
    .min(2, "Username must be at least 2 characters")
    .max(30, "Username must not exceed 30 characters")
    .matches(
      /^[a-zA-Z\s-]+$/,
      "Username can only contain letters, spaces, and hyphens"
    )
    .required("Username is required"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .max(50, "Email must not exceed 50 characters")
    .required("Email is required"),
  password: Yup.string()
    .min(6, "Password must be at least 6 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      "Password must contain at least one uppercase letter, one lowercase letter, and one number"
    )
    .required("Password is required"),
  confirm_password: Yup.string()
    .oneOf([Yup.ref("password"), null], "Passwords must match")
    .required("Confirm password is required"),
});

export const addIssueValidationSchema = Yup.object().shape({
  // vehicle_id: Yup.number()
  //     .required('Vehicle is required'),
  // service_id: Yup.number()
  //     .required('Service ID is required'),
  reported_by: Yup.number().required("Reported by is required"),
  assign_to: Yup.number().required("Assigned to is required"),
  odo_meter: Yup.number()
    .min(0, "Odometer reading must be zero or positive")
    .required("Odometer reading is required"),
  priority: Yup.string()
    .oneOf(["low", "medium", "high"], "Priority must be low, medium, or high")
    .required("Priority is required"),
  summery: Yup.string()
    .max(255, "Summary must not exceed 255 characters")
    .required("Summary is required"),
  description: Yup.string().max(
    1000,
    "Description must not exceed 1000 characters"
  ),
  reported_at: Yup.date().required("Reported date is required"),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const IssueValidationSchema = Yup.object().shape({
  vehicle_id: Yup.number().required("Vehicle is required"),
  reported_by: Yup.number().required("Reported by is required"),
  assign_to: Yup.number().required("Assigned to is required"),
  odm: Yup.number()
    .typeError("Odometer reading / distance must be a number")
    .min(0, "Odometer reading / distance must be zero or positive")
    .required("Odometer reading / distance is required"),
  // .test(
  //   "min-last-recorded",
  //   "Odometer reading cannot be less than last recorded value",
  //   function (value) {
  //     const { odo_meter } = this.options.context || {};

  //     if(!odo_meter) return true

  //     const lastOdo = parseInt(odo_meter);
  //     if (typeof value !== "number") return true;
  //     return value >= parseInt(lastOdo);
  //   }
  // ),
  priority: Yup.string()
    .oneOf(["low", "medium", "high"], "Priority must be low, medium, or high")
    .required("Priority is required"),
  summery: Yup.string()
    .max(255, "Summary must not exceed 255 characters")
    .required("Summary is required"),
  description: Yup.string().max(
    1000,
    "Description must not exceed 1000 characters"
  ),
  reported_at: Yup.date().required("Reported date is required"),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const IssueCreateValidationSchema = Yup.object().shape({
  asset_type: Yup.string()
    .oneOf(["vehicle", "equipment", "tyre"], "Asset type is invalid")
    .required("Asset type is required"),
  asset_id: Yup.mixed().test(
    "asset-id-required",
    "Asset is required",
    (value) => value !== "" && value !== null && value !== undefined
  ),
  reported_by: Yup.number().required("Reported by is required"),
  assign_to: Yup.number().required("Assigned to is required"),
  odm: Yup.number()
    .typeError("Odometer reading / distance must be a number")
    .min(0, "Odometer reading / distance must be zero or positive")
    .required("Odometer reading / distance is required"),
  priority: Yup.string()
    .oneOf(["low", "medium", "high"], "Priority must be low, medium, or high")
    .required("Priority is required"),
  summery: Yup.string()
    .max(255, "Summary must not exceed 255 characters")
    .required("Summary is required"),
  description: Yup.string().max(
    1000,
    "Description must not exceed 1000 characters"
  ),
  reported_at: Yup.date().required("Reported date is required"),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const WorkOrderValidationSchema = Yup.object().shape({
  asset_type: Yup.string()
    .oneOf(["vehicle", "equipment", "tyre"], "Asset type is invalid")
    .required("Asset type is required"),
  asset_id: Yup.mixed().test(
    "asset-id-required",
    "Asset is required",
    (value) => value !== "" && value !== null && value !== undefined
  ),
  repair_class: Yup.string().required("Repair class is required"),
  issued_at: Yup.date().required("Issued At is required"),
  scheduled_start_at: Yup.date()
    .nullable()
    .required("Scheduled Start Date is required"),
  expected_completion_at: Yup.date()
    .nullable()
    .required("Expected Completion Date is required"),
  issued_by: Yup.string().required("Issued By is required"),
  assigned_to: Yup.string().required("Assignee is required"),
  vendor_id: Yup.string().required("Vendor is required"),
  invoice_no: Yup.string().required("Invoice Number is required"),
  service_ids: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const CreateWorkOrderValidationSchema = Yup.object().shape({
  asset_type: Yup.string()
    .oneOf(["vehicle", "equipment", "tyre"], "Asset type is invalid")
    .required("Asset type is required"),
  asset_id: Yup.mixed().test(
    "asset-id-required",
    "Asset is required",
    (value) => value !== "" && value !== null && value !== undefined
  ),
  repair_class: Yup.string().required("Repair class is required"),
  issued_at: Yup.date().required("Issued At is required"),
  scheduled_start_at: Yup.date()
    .nullable()
    .required("Scheduled Start Date is required"),
  expected_completion_at: Yup.date()
    .nullable()
    .required("Expected Completion Date is required"),
  issued_by: Yup.string().required("Issued By is required"),
  assigned_to: Yup.string().required("Assignee is required"),
  vendor_id: Yup.string().required("Vendor is required"),
  invoice_no: Yup.string().required("Invoice Number is required"),
  service_ids: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const serviceEntryValidationSchema = Yup.object().shape({
  vehicle_id: Yup.number().required("Vehicle is required"),
  odm: Yup.number()
    .min(0, "Odometer reading must be zero or positive")
    .required("Odometer reading is required"),
  // .test(
  //   "min-last-recorded",
  //   "Odometer reading cannot be less than last recorded value",
  //   function (value) {
  //     const { odo_meter } = this.options.context || {};

  //     if (!odo_meter) return true;

  //     const lastOdo = parseInt(odo_meter);
  //     if (typeof value !== "number") return true;
  //     return value >= parseInt(lastOdo);
  //   }
  // ),
  priority: Yup.string()
    .oneOf(["low", "medium", "high"], "Priority must be low, medium, or high")
    .required("Priority is required"),
  services_type: Yup.string()
    .oneOf(
      ["scheduled", "unscheduled"],
      "Service type must be scheduled or unscheduled"
    )
    .required("Service type is required"),
  start_at: Yup.date().nullable().transform(emptyDateToNull),
  complete_at: Yup.date()
    .nullable()
    .transform(emptyDateToNull)
    .required("Completion date is required")
    .test(
      "completion-not-before-start",
      completionDateAfterStartDateMessage,
      function (value) {
        return isDateOnOrAfter(value, this.parent?.start_at);
      }
    ),
  note: Yup.string().max(1000, "Notes must not exceed 1000 characters"),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const createServiceEntryValidationSchema = Yup.object().shape({
  asset_type: Yup.string()
    .oneOf(["vehicle", "equipment", "tyre"], "Asset type is invalid")
    .required("Asset type is required"),
  asset_id: Yup.mixed().test(
    "asset-id-required",
    "Asset is required",
    (value) => value !== "" && value !== null && value !== undefined
  ),
  odm: Yup.number().when("asset_type", {
    is: (assetType) => Boolean(assetType),
    then: (schema) =>
      schema
        .typeError("Odometer reading / distance must be a number")
        .min(0, "Odometer reading / distance must be zero or positive")
        .required("Odometer reading / distance is required"),
    otherwise: (schema) => schema.nullable().notRequired(),
  }),
  priority: Yup.string()
    .oneOf(["low", "medium", "high"], "Priority must be low, medium, or high")
    .required("Priority is required"),
  services_type: Yup.string()
    .oneOf(
      ["scheduled", "unscheduled"],
      "Service type must be scheduled or unscheduled"
    )
    .required("Service type is required"),
  start_at: Yup.date().nullable().transform(emptyDateToNull),
  complete_at: Yup.date()
    .nullable()
    .transform(emptyDateToNull)
    .required("Completion date is required")
    .test(
      "completion-not-before-start",
      completionDateAfterStartDateMessage,
      function (value) {
        return isDateOnOrAfter(value, this.parent?.start_at);
      }
    ),
  note: Yup.string().max(1000, "Notes must not exceed 1000 characters"),
  tags: Yup.array()
    .of(
      Yup.object().shape({
        value: Yup.string(),
        label: Yup.string(),
      })
    )
    .nullable(),
});

export const serviceLineSchema = Yup.object().shape({
  cost_parts: Yup.number()
    .min(0, "Parts cost must be zero or positive")
    .required("Parts cost is required"),
  rate: Yup.number()
    .min(0, "Unit Price must be zero or positive")
    .required("Unit Price is required"),
  qty: Yup.number()
    .min(0, "Quantity must be zero or positive")
    .required("Quantity is required"),
  cost_labor: Yup.number()
    .min(0, "Labor must be zero or positive")
    .required("Labor is required"),
  sub_total: Yup.number()
    .min(0, "Subtotal must be zero or positive")
    .required("Subtotal is required"),
  service_category_id: Yup.number().required("Category is required"),
  repair_reason_id: Yup.number().required("Reason for repair is required"),
  odo_meter: Yup.number()
    .min(0, "Odometer reading must be zero or positive")
    .required("Odometer reading is required"),
  description: Yup.string().max(
    500,
    "Description must not exceed 500 characters"
  ),
});

export const pettyCashSchema = Yup.object().shape({
  amount: Yup.number()
    .moreThan(0, "Amount must be greater than 0")
    .required("Amount is required"),
  payment_mode: Yup.string().required("Payment mode is required"),
  transaction_id: Yup.string().when('payment_mode', {
    is: (value) => {
      const normalized = String(value || '').trim().toLowerCase().replace(/\s+/g, '_');
      return Boolean(normalized) && normalized !== 'cash';
    },
    then: (schema) => schema.required('Transaction ID is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  assign_at: Yup.date().required("Date is required"),
  source: Yup.string().required("Source is required"),
  source_id: Yup.mixed().when('source', {
    is: (value) => {
      const normalized = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
      return (
        normalized === 'filling_station' ||
        normalized === 'customer' ||
        normalized === 'transporter'
      );
    },
    then: (schema) => schema.required('Source selection is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  location: Yup.object().when('source', {
    is: (value) => {
      const normalized = String(value || '').trim().toLowerCase().replace(/[-\s]+/g, '_');
      return (
        normalized !== 'filling_station' &&
        normalized !== 'customer' &&
        normalized !== 'transporter'
      );
    },
    then: (schema) => schema.shape({
      point: Yup.string().required("Location is required"),
      latitude: Yup.string().required("Latitude is required"),
      longitude: Yup.string().required("Longitude is required"),
    }),
    otherwise: (schema) => schema.shape({
      point: Yup.string().nullable(),
      latitude: Yup.string().nullable(),
      longitude: Yup.string().nullable(),
    }),
  }),

  description: Yup.string().max(1000, "Description must not exceed 1000 characters").optional(),
});

export const tripExpenseSchema = Yup.object().shape({
  amount: Yup.number()
    .min(0, "Amount must be zero or positive")
    .required("Amount is required"),
  trip_expense_category: Yup.string().required("Trip Expense Category is required"),
});

// ========================== PART FORM ===========================

// An empty numeric input reaches Yup as "", which Yup.number() rejects during
// the cast with a typeError ("must be a number") before required()/nullable()
// ever run. Normalising "" to null lets those decide the real message.
const emptyToNull = (value, originalValue) =>
  String(originalValue ?? '').trim() === '' ? null : value;

export const partStepSchemas = [
  // Step 0: General Details
  Yup.object().shape({
    description: Yup.string()
      .test('description-required', 'Description is required', (value) => {
        const plainText = String(value || '')
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        return plainText.length > 0;
      })
      .test('description-max', 'Description too long', (value) => {
        const plainText = String(value || '')
          .replace(/<[^>]*>/g, ' ')
          .replace(/&nbsp;/gi, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        return plainText.length <= 1000;
      }),
    part_name: Yup.string().required('Part Name is required'),
    company_name: Yup.string().required('Company Name is required'),
    part_number: Yup.string().required('Part Number is required'),
    sku: Yup.string().required('SKU is required'),
    category: Yup.string().required('Category is required'),
  }),

  // Step 1: Specifications
  Yup.object().shape({
    material: Yup.string().required('Material is required'),
    weight: Yup.number().transform(emptyToNull).typeError('Weight must be a number').min(0, 'Must be >= 0').required('Weight is required'),
    height: Yup.number().transform(emptyToNull).typeError('Height must be a number').min(0, 'Must be >= 0').required('Height is required'),
    width: Yup.number().transform(emptyToNull).typeError('Width must be a number').min(0, 'Must be >= 0').required('Width is required'),
    length: Yup.number().transform(emptyToNull).typeError('Length must be a number').min(0, 'Must be >= 0').required('Length is required'),

    make: Yup.string().required('Make is required'),
    model: Yup.string().required('Model is required'),
    year: Yup.number().transform(emptyToNull).typeError('Year must be a number').min(0, 'Must be >= 0').required('Year is required'),
    engineCode: Yup.string().required('Engine Code is required'),
    crossReferenceSku: Yup.string().required('Cross Reference SKU is required'),
    maxPressure: Yup.string().required('Max Pressure is required'),
    maxVoltage: Yup.string().required('Max Voltage is required'),
    maxTemperature: Yup.string().required('Max Temperature is required'),
  }),

  // Step 2: Measurement
  Yup.object().shape({
    unitOfMeasurement: Yup.string().required('Unit of Measurement is required'),
  }),

  // Step 3: Stocks
  Yup.object().shape({
    safetyStockThreshold: Yup.number().transform(emptyToNull).typeError('Safety stock threshold must be a number').min(0, 'Must be >= 0').required('Safety stock threshold is required'),
    availableStock: Yup.number().transform(emptyToNull).typeError('Available stock must be a number').min(0, 'Must be >= 0').required('Available stock is required'),
    price: Yup.number().transform(emptyToNull).typeError('Unit price must be a number').min(0, 'Must be >= 0').nullable(),
    // vendor: Yup.number().required('Vendor is required')
  })
];

// The Part Details (edit) screen shows every step's fields on one page, so it
// validates against all of them at once. Derived from partStepSchemas so the
// two screens can never drift apart.
export const partFullSchema = partStepSchemas.reduce(
  (merged, stepSchema) => merged.concat(stepSchema),
  Yup.object(),
);

export const categoryValidationSchema = Yup.object().shape({
    title: Yup.string()
        .min(2, "Title must be at least 2 characters")
        .max(50, "Title must not exceed 50 characters")
        .required("Title is required"),
    status: Yup.string().required("Status is required"),
    type: Yup.string().required("Type is required"),
});

export const vehicleTypeValidationSchema = Yup.object().shape({
    title: Yup.string()
        .min(2, "Title must be at least 2 characters")
        .max(50, "Title must not exceed 50 characters")
        .required("Title is required"),
    status: Yup.string().required("Status is required"),
    vehicle_class: Yup.string().required("Vehicle class is required"),
});

export const partModelValidationSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Name must be at least 2 characters")
        .max(50, "Name must not exceed 50 characters")
        .required("Name is required"),
    status: Yup.string().required("Status is required"),
});

export const partMakeValidationSchema = Yup.object().shape({
    name: Yup.string()
        .min(2, "Name must be at least 2 characters")
        .max(50, "Name must not exceed 50 characters")
        .required("Name is required"),
    status: Yup.string().required("Status is required"),
});

export const contentValidationSchema = Yup.object().shape({
    title: Yup.string()
        .trim()
        .required("Title is required"),
    category: Yup.string().required("Category is required"),
    type: Yup.string()
        .oneOf(["video", "pdf"], "Type must be either Video or PDF")
        .required("Type is required"),
    fileUrl: Yup.string().trim().required("File is required"),
});






// ========================== TRIP FORM (Multi-step) ===========================

export const tripStepSchemas = [
  // Step 0: General Details
  Yup.object().shape({
    customer: Yup.string().required('Customer is required'),
    contractId: Yup.string().optional().nullable(),
    vehicleType: Yup.string().required('Vehicle Type is required'),
    costCenter: Yup.number().typeError('Cost Center must be a number').required('Cost Center is required'),
    loadType: Yup.string().nullable(),
    startDateTime: Yup.date().required('Start date & time is required'),
    expectedEndDateTime: Yup.date()
      .min(Yup.ref('startDateTime'), 'Expected end must be after start')
      .required('Expected end date & time is required'),
    description: Yup.string().max(1000, 'Description too long').notRequired(),
  }),

  // Step 1: Stop Trip
  Yup.object().shape({
    stops: Yup.array()
      .of(
        Yup.object().shape({
          location: Yup.object().shape({
            location_point: Yup.string().required("Location is required"),
            location_latitude: Yup.string().required("Location latitude is required"),
            location_longitude: Yup.string().required("Location longitude is required"),
          }),
          stopType: Yup.string().required("Stop Type is required"),
          loadType: tripStopLoadTypeSchema,
          product: tripStopProductSchema,
          quantity: tripStopQuantitySchema,
        })
      )
      .min(2, "Minimum 2 stops are required")
      .required("Stop Trip is required")
      .test(
        "unload-quantity-match",
        "Total unload quantity must equal total load quantity",
        function (stops) {
          if (!Array.isArray(stops)) return true;

          const totalLoad = stops
            .filter((stop) => String(stop?.stopType || "").toLowerCase() === "load")
            .reduce((sum, stop) => sum + (Number(stop?.quantity) || 0), 0);

          const totalUnload = stops
            .filter((stop) => String(stop?.stopType || "").toLowerCase() === "unload")
            .reduce((sum, stop) => sum + (Number(stop?.quantity) || 0), 0);

          if (totalLoad === 0 && totalUnload === 0) return true;

          if (totalUnload > totalLoad) {
            return this.createError({
              message: `Your load quantity is ${totalLoad}. You have entered more than this.`,
            });
          }

          if (totalUnload < totalLoad) {
            const remaining = totalLoad - totalUnload;
            return this.createError({
              message: `Total unload quantity is less than load quantity. ${remaining} quantity remaining.`,
            });
          }

          return true;
        }
      ),
  }),

  // Step 2: Vehicle & Fuel
  Yup.object().shape({
    vehicle: Yup.string().required('Vehicle is required'),
    openingFuel: Yup.number().typeError('Opening fuel must be a number').min(0, 'Must be >= 0').required('Opening fuel is required'),
    allocatedFuel: Yup.number().typeError('Allocated fuel must be a number').min(0, 'Must be >= 0').required('Allocated fuel is required'),
    allocatedPettyCash: Yup.number().typeError('Allocated petty cash must be a number').min(0, 'Must be >= 0').required('Allocated petty cash is required'),
    startOdometer: Yup.number().typeError('Odometer reading must be a number').required('Start odometer is required'),
    uploadedBy: Yup.string().required('Uploaded by is required'),
    uploadDate: Yup.date().required('Upload date is required'),
  }),

  // Step 3: Driver Info
  Yup.object().shape({
    firstDriver: Yup.string().when('hiredDriver', {
      is: (val) => Boolean(val && String(val).trim()),
      then: (schema) => schema.nullable().notRequired(),
      otherwise: (schema) => schema.required('First driver is required'),
    }),
    firstDriverContact: Yup.string().when('hiredDriver', {
      is: (val) => Boolean(val && String(val).trim()),
      then: (schema) => schema.nullable().notRequired(),
      otherwise: () => buildPhoneNumberSchema('Contact is required'),
    }),
    hiredDriver: Yup.string().nullable().notRequired(),
    hiredDriverContact: Yup.string().when('hiredDriver', {
      is: (val) => Boolean(val && String(val).trim()),
      then: () => buildPhoneNumberSchema('Contact is required'),
      otherwise: () => optionalPhoneNumberSchema,
    }),
    supplementaryDriverContact: optionalPhoneNumberSchema,
    helperContact: optionalPhoneNumberSchema,
  }),
];

export const tripDetailSchema = Yup.object().shape({
  status: Yup.string().required('Status is required'),
  customer: Yup.string().required('Customer is required'),
  contractId: Yup.string().optional().nullable(),
  vehicleType: Yup.string().required('Vehicle Type is required'),
  costCenter: Yup.number().typeError('Cost Center must be a number').required('Cost Center is required'),
  startDateTime: Yup.date().required('Start date & time is required'),
  expectedEndDateTime: Yup.date()
    .min(Yup.ref('startDateTime'), 'Expected end must be after start')
    .required('Expected end date & time is required'),
  actualEndDateTime: Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue ? value : null))
    .min(Yup.ref('startDateTime'), 'Actual end must be after start'),
  stops: Yup.array()
    .of(
      Yup.object().shape({
        location: Yup.object().shape({
          location_point: Yup.string().required("Location is required"),
          location_latitude: Yup.string().required("Location latitude is required"),
          location_longitude: Yup.string().required("Location longitude is required"),
        }),
        stopType: Yup.string().required("Stop Type is required"),
        loadType: tripStopLoadTypeSchema,
        product: tripStopProductSchema,
        quantity: tripDetailStopQuantitySchema,
      })
    )
    .min(2, "Minimum 2 stops are required")
    .required("At least one stop is required"),
  firstDriver: Yup.string().required('First driver is required'),
  firstDriverContact: buildPhoneNumberSchema('Driver contact is required'),
  supplementaryDriverContact: optionalPhoneNumberSchema,
  helperContact: optionalPhoneNumberSchema,
  vehicle: Yup.string().required('Vehicle is required'),
  openingFuel: Yup.number().typeError('Opening fuel must be a number').min(0, 'Must be >= 0').required('Opening fuel is required'),

  allocatedFuel: Yup.number().typeError('Allocated fuel must be a number').min(0, 'Must be >= 0').required('Allocated fuel is required'),
  allocatedPettyCash: Yup.number().typeError('Allocated petty cash must be a number').min(0, 'Must be >= 0').required('Allocated petty cash is required'),

  startOdometer: Yup.number().typeError('Odometer reading must be a number').required('Start odometer is required'),
  endOdometer: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return null;
      }

      return value;
    })
    .nullable()
    .typeError('End Odometer must be a number')
    .test(
      'greater-than-start-odometer',
      'End Odometer Reading must be greater than Start Odometer',
      function (value) {
        if (value === null || value === undefined) return true;

        const { startOdometer } = this.parent;
        if (startOdometer === "" || startOdometer === null || startOdometer === undefined) {
          return true;
        }

        const parsedStartOdometer = Number(startOdometer);
        if (Number.isNaN(parsedStartOdometer)) return true;

        return value > parsedStartOdometer;
      }
    ),
  closing_fuel: Yup.number()
    .transform((value, originalValue) => {
      if (originalValue === "" || originalValue === null || originalValue === undefined) {
        return 0;
      }

      return value;
    })
    .typeError('Closing fuel must be a number')
    .min(0, 'Must be >= 0'),
  uploadedBy: Yup.string().required('Uploaded by is required'),
  uploadDate: Yup.date().required('Upload date is required'),
});

export const addFuelLogValidationSchema = Yup.object().shape({
  trip_id: Yup.string().required("Trip ID is required"),
  vehicle_id: Yup.string().required("Vehicle is required"),
  fuelType: Yup.string().required("Fuel type is required"),
  filled_at: Yup.date().required("Date is required"),
  quantity: Yup.number()
    .typeError("Quantity must be a number")
    .positive("Quantity must be positive")
    .required("Quantity is required"),
  fuel_price: Yup.number()
    .nullable()
    .notRequired()
    .typeError("Fuel price must be a number")
    .positive("Fuel price must be positive"),
  total_amount: Yup.number()
    .typeError("Total amount must be a number")
    .positive("Total amount must be positive")
    .required("Total amount is required"),
  vendor_id: Yup.string().required("Filling Station is required"),
  attachments: Yup.array()
    .min(1, "At least one attachment is required")
    .required("At least one attachment is required"),
});

export const VendorSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  address: Yup.object().shape({
    vendor_point: Yup.string().required('Address is required'),
    vendor_latitude: Yup.string(),
    vendor_longitude: Yup.string()
  }).required('Address is required'),
  pocName: Yup.string().required('POC Name is required'),
  pocContact: buildPhoneNumberSchema('POC Contact is required'),
  pocEmail: Yup.string().email('Invalid email').required('POC Email is required'),
  category: Yup.array().min(1, 'At least one category is required').required('Category is required'),
});

export const WarehouseSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  address: Yup.object().shape({
    warehouse_point: Yup.string().required('Address is required'),
    warehouse_latitude: Yup.string(),
    warehouse_longitude: Yup.string()
  }).required('Address is required'),
  pocName: Yup.string().required('POC Name is required'),
  pocContact: buildPhoneNumberSchema('POC Contact is required'),
  pocEmail: Yup.string().email('Invalid email').required('POC Email is required'),
});

export const RFQSchema = Yup.object().shape({
  requested_by: Yup.object().nullable().required("Requester is required"),
  target_delivery_date: Yup.string().required("Target Delivery Date is required"),
  date_created: Yup.string().required("Date Created is required"),
  attachments: Yup.array()
    .min(1, "At least one attachment is required")
    .required("At least one attachment is required"),
  notes: Yup.string().nullable().notRequired(),
  items: Yup.array()
    .of(
      Yup.object().shape({
        part_id: Yup.mixed().required(),
        quantity: Yup.number().required("Required").min(1, "Min 1"),
      })
    )
    .min(1, "At least one part is required"),
});

export const RFQUpdateSchema = Yup.object().shape({
  target_delivery_date: Yup.string().required("Target Delivery Date is required"),
  notes: Yup.string().nullable().notRequired(),
  items: Yup.array().of(
    Yup.object().shape({
      quantity: Yup.number()
        .transform((value, originalValue) => (String(originalValue).trim() === "" ? undefined : value))
        .required('Required')
        .integer('Must be an integer')
        .min(1, 'Min 1')
    })
  ).min(1, "At least one item is required"),
});
export const FuelStationSchema = Yup.object().shape({
  name: Yup.string().required('Station Name is required'),
  contact: buildPhoneNumberSchema('Contact is required'),
  email: Yup.string().email('Invalid email').required('Email is required'),
  location_info: Yup.object().shape({
    station_point: Yup.string().required('Location is required'),
    station_latitude: Yup.number().required('Latitude is required'),
    station_longitude: Yup.number().required('Longitude is required'),
  }),
  fuel_company_id: Yup.number().required('Fuel Company is required'),
  contract_start_at: Yup.date().required('Contract Start Date is required'),
  contract_end_at: Yup.date()
    .min(Yup.ref('contract_start_at'), 'Contract End must be after Start')
    .required('Contract End Date is required'),
  fuel_rate_at: Yup.date().nullable().notRequired(),
  rates: Yup.array().of(
    Yup.object().shape({
      fuel_type: Yup.string().required('Fuel type is required'),
      price: Yup.number()
        .transform((value, originalValue) => originalValue === '' ? undefined : value)
        .typeError('Price must be a number')
        .min(0, 'Price must be zero or positive')
        .required('Price is required'),
    })
  )
});

const RFQ_BID_MAX_AMOUNT = 99999999.99;
const RFQ_BID_MAX_AMOUNT_ERROR = "The amount is too large. Enter a valid amount";

export const RFQRequestSchema = Yup.object().shape({
  deliveryType: Yup.string().required('Delivery Type is required'),
  deliveryDate: Yup.date().nullable().required('Delivery Date is required'),
  discount: Yup.number()
    .transform((value, original) => (original === '' || original === null || original === undefined ? 0 : value))
    .typeError('Must be a number')
    .min(0, 'Min 0')
    .max(100, 'Max 100'),
  shipping: Yup.number()
    .transform((value, original) => (original === '' || original === null || original === undefined ? 0 : value))
    .typeError('Must be a number')
    .min(0, 'Min 0')
    .max(RFQ_BID_MAX_AMOUNT, RFQ_BID_MAX_AMOUNT_ERROR),
  tax: Yup.number()
    .transform((value, original) => (original === '' || original === null || original === undefined ? 0 : value))
    .typeError('Must be a number')
    .min(0, 'Min 0')
    .max(100, 'Max 100'),
  items: Yup.array().of(
    Yup.object().shape({
      unitPrice: Yup.number()
        .transform((value, original) => original === "" ? null : value)
        .nullable()
        .typeError('Must be a number')
        .required('Required')
        .min(0, 'Min 0')
        .max(RFQ_BID_MAX_AMOUNT, RFQ_BID_MAX_AMOUNT_ERROR)
        .test(
          'rfq-line-total-max',
          RFQ_BID_MAX_AMOUNT_ERROR,
          function (value) {
            if (value === null || value === undefined || value === "") return true;
            const quantity = Number(this.parent?.quantity ?? 0);
            const unitPrice = Number(value);
            if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) return true;
            return (quantity * unitPrice) <= RFQ_BID_MAX_AMOUNT;
          }
        )
    })
  )
});

export const TransferStockSchema = Yup.object().shape({
  destination_warehouse: Yup.object().nullable().required('Destination Warehouse is required'),
  items: Yup.array()
    .of(
      Yup.object().shape({
        part_id: Yup.number().required('Part is required'),
        quantity: Yup.number()
          .transform((value, originalValue) => (String(originalValue).trim() === '' ? undefined : value))
          .typeError('Quantity must be a number')
          .integer('Quantity must be an integer')
          .min(1, 'Min 1')
          .test(
            'stock-limit',
            'Quantity exceeds available stock',
            function (value) {
              const availableQty = Number(this.parent?.available_qty);
              if (value === undefined || value === null) return true;
              if (!Number.isFinite(availableQty) || availableQty <= 0) {
                return this.createError({ message: 'No stock available for transfer' });
              }
              return Number(value) <= availableQty;
            }
          )
          .required('Quantity is required')
      })
    )
    .min(1, 'At least one part is required'),
});

export const POPaymentTransactionSchema = Yup.object().shape({
  amount: Yup.number()
    .transform((value, originalValue) => (String(originalValue).trim() === "" ? undefined : value))
    .typeError('Amount must be a number')
    .positive('Amount must be greater than 0')
    .required('Amount is required'),
  payment_method: Yup.string()
    .oneOf(['cash', 'bank_transfer', 'cheque'], 'Invalid payment method')
    .required('Payment method is required'),
  bank_name: Yup.string().when('payment_method', {
    is: 'bank_transfer',
    then: (schema) => schema.required('Bank Name is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  account_title: Yup.string().when('payment_method', {
    is: 'bank_transfer',
    then: (schema) => schema.required('Account Title is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  iban_number: Yup.string().when('payment_method', {
    is: 'bank_transfer',
    then: (schema) => schema.matches(/^[A-Z0-9]+$/i, 'Enter a valid IBAN number').required('IBAN Number is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  reference_number: Yup.string().nullable(),
  files: Yup.array().of(Yup.string()),
  fileObjects: Yup.array()
    .min(1, 'At least one attachment is required')
    .required('At least one attachment is required'),
});

export const AddFleetStatusEntryValidationSchema = Yup.object().shape({
  vehicle_id: Yup.mixed().test(
    "vehicle-required",
    "Registration No. is required",
    (value) => value !== "" && value !== null && value !== undefined
  ),
  status: Yup.string().required("Current Status is required"),
  status_date: Yup.date()
    .typeError("Status Date is invalid")
    .required("Status Date is required"),
  status_time: Yup.string().required("Status Time is required"),
  location: Yup.string().required("Current Location is required"),
  customer_id: Yup.mixed().nullable(),
  origin: Yup.string(),
  destination: Yup.string(),
  remarks: Yup.string().max(500, "Remarks must not exceed 500 characters"),
});
